import React from 'react';
import { useNavigate } from 'react-router';
import { OnboardingTask, OnboardingTaskKey } from 'types/interfaces/onboarding';
import ChecklistTask from './ChecklistTask';
import './OnboardingChecklist.css';

interface OnboardingChecklistProps {
  tasks: OnboardingTask[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onStartTask: (task: OnboardingTask) => void;
  onClaimPoints: (taskKey: OnboardingTaskKey) => void;
  onConfirmSocialFollow: (taskKey: OnboardingTaskKey) => void;
  onCancelSocialFollow: () => void;
  completingTaskKey: OnboardingTaskKey | null;
  claimingTaskKey: OnboardingTaskKey | null;
  animatingTaskKey: OnboardingTaskKey | null;
  onAnimationComplete: (taskKey: OnboardingTaskKey) => void;
  isConfirming: boolean;
  isClaiming: boolean;
  onDismiss?: () => void;
  isDismissing?: boolean;
}

const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  tasks,
  isLoading,
  isError,
  onRetry,
  onStartTask,
  onClaimPoints,
  onConfirmSocialFollow,
  onCancelSocialFollow,
  completingTaskKey,
  claimingTaskKey,
  animatingTaskKey,
  onAnimationComplete,
  isConfirming,
  isClaiming,
  onDismiss,
  isDismissing = false,
}) => {
  const navigate = useNavigate();
  void navigate; // Suppress unused variable warning - kept for future use
  // Filter tasks:
  // - Show all non-completed tasks
  // - Show completed tasks that haven't had points claimed yet (claimable)
  // - Show animating tasks (during claim animation)
  // - Hide fully completed tasks (completed AND points awarded)
  const visibleTasks = tasks.filter(task => {
    // Always show animating tasks
    if (task.taskKey === animatingTaskKey) return true;
    // Show incomplete tasks
    if (task.status !== 'COMPLETED') return true;
    // Show completed but unclaimed tasks (claimable)
    if (task.status === 'COMPLETED' && !task.pointsAwarded) return true;
    // Hide fully completed tasks
    return false;
  });

  // Check if all tasks are fully completed (completed AND points claimed)
  const allTasksFullyComplete = tasks.length > 0 && tasks.every(
    task => task.status === 'COMPLETED' && task.pointsAwarded
  );

  // Show loading skeleton
  if (isLoading) {
    return (
      <div className="onboarding-checklist">
        <div className="checklist-header">
          <h3>Complete your setup</h3>
        </div>
        <div className="checklist-tasks">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="checklist-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="onboarding-checklist">
        <div className="checklist-header">
          <h3>Complete your setup</h3>
        </div>
        <div className="checklist-error">
          <p>Unable to load your progress. Please try again.</p>
          <button onClick={onRetry} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state when all tasks are fully completed (completed AND points claimed)
  if (allTasksFullyComplete && visibleTasks.length === 0) {
    return (
      <div className={`onboarding-checklist setup-complete-card ${isDismissing ? 'dismissing' : ''}`}>
        <div className="checklist-header">
          <h3>Setup Complete</h3>
        </div>
        <div className="checklist-complete">
          <div className="complete-icon">🎉</div>
          <p>You're all set! Time to start trading cards with the community.</p>
          <div className="complete-actions">
            <button
              className="complete-action-btn primary"
              onClick={onDismiss}
              disabled={isDismissing}
            >
              Let's do it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-checklist">
      <div className="checklist-header">
        <h3>Complete your setup</h3>
      </div>
      <div className="checklist-tasks" role="list">
        {visibleTasks.map((task) => (
          <ChecklistTask
            key={task.taskKey}
            task={task}
            onStartTask={onStartTask}
            onClaimPoints={onClaimPoints}
            onConfirmSocialFollow={onConfirmSocialFollow}
            onCancelSocialFollow={onCancelSocialFollow}
            isConfirmingFollow={completingTaskKey === task.taskKey}
            isAnimating={animatingTaskKey === task.taskKey}
            onAnimationComplete={onAnimationComplete}
            isConfirming={isConfirming && completingTaskKey === task.taskKey}
            isClaiming={isClaiming && claimingTaskKey === task.taskKey}
          />
        ))}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
